import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider, useQuery } from "@tanstack/react-query";
import { HashRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { useLanguagePersistence } from "./hooks/useLanguagePersistence";
import { useKeyboardShortcuts } from "./hooks/useKeyboardShortcuts";
import { AnalyticsManager } from "@/components/analytics/AnalyticsManager";
import { MetaPixelManager } from "@/components/analytics/MetaPixelManager";
import { ScrollToTop } from "@/components/layout/ScrollToTop";
import { ConditionalLegalFooter } from "@/components/legal/ConditionalLegalFooter";
import { CookieConsentManager } from "@/components/cookies/CookieConsentManager";
import getPreferences from "./logic/preferences/getPreferences";
import { postLoginRouteFromUserPreferences, USER_PREFERENCES_QUERY_KEY } from "./logic/preferences";

// Lazy load route components for better performance
const Index = lazy(() => import("./pages/Index"));
const Login = lazy(() => import("./pages/Login"));
const AuthCallback = lazy(() => import("./pages/AuthCallback"));
const Register = lazy(() => import("./pages/Register"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const VerifyEmailChange = lazy(() => import("./pages/VerifyEmailChange"));
const Preferences = lazy(() => import("./pages/Preferences"));
const WeeklyPlanner = lazy(() => import("./pages/WeeklyPlanner"));
// Inici (`/inici`): nothing links here yet, and only the people the server's list answers see it.
const Home = lazy(() => import("./pages/Home"));
const Inspiration = lazy(() => import("./pages/Inspiration"));
const Recipe = lazy(() => import("./pages/Recipe"));
const ShoppingList = lazy(() => import("./pages/ShoppingList"));
const CheckoutAddress = lazy(() => import("./pages/CheckoutAddress"));
const Orders = lazy(() => import("./pages/Orders"));
const OrderDetails = lazy(() => import("./pages/OrderDetails"));
const PaymentSuccess = lazy(() => import("./pages/PaymentSuccess"));
const Profile = lazy(() => import("./pages/Profile"));
const About = lazy(() => import("./pages/About"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));
const LegalNotice = lazy(() => import("./pages/LegalNotice"));
const NotFound = lazy(() => import("./pages/NotFound"));
const AdminDashboard = lazy(() => import("./pages/admin/Dashboard"));
const AdminWeeklyPlans = lazy(() => import("./pages/admin/WeeklyPlans"));
const AdminOrders = lazy(() => import("./pages/admin/Orders"));
const AdminUsers = lazy(() => import("./pages/admin/Users"));
const AdminRecipes = lazy(() => import("./pages/admin/recipes/Recipes"));
const AdminCanonicalIngredients = lazy(() => import("./pages/admin/canonical-ingredients/CanonicalIngredients"));
const AdminAllergens = lazy(() => import("./pages/admin/Allergens"));
const AdminRecipeCoverage = lazy(() => import("./pages/admin/recipe-coverage/RecipeCoverage"));

// Loading fallback component
const PageLoader = () => (
  <main className="flex items-center justify-center min-h-screen" role="main" aria-label="Loading">
    <div className="text-center">
      <div
        className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"
        role="status"
        aria-live="polite"
      ></div>
      <p className="mt-4 text-foreground/70">Loading...</p>
    </div>
  </main>
);

const SmartAuthRedirect = () => {
  const { data, isError, status } = useQuery({
    queryKey: USER_PREFERENCES_QUERY_KEY,
    queryFn: getPreferences,
  });

  if (status === "pending") {
    return <PageLoader />;
  }
  if (isError) {
    return <Navigate to="/preferences" replace />;
  }
  const route = postLoginRouteFromUserPreferences(data, false);
  return <Navigate to={route} replace />;
};

/**
 * ✅ PERF: global defaults tuned to stop refetch thrashing on every tab focus
 * and reuse cached data across route changes (e.g. shopping list → recipe → back).
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 min — data is considered fresh
      gcTime: 10 * 60 * 1000, // 10 min — keep in memory after unmount
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: 1,
    },
  },
});

// Component that uses router hooks - must be inside HashRouter
const RoutesWithHooks = ({ isAuthenticated }: { isAuthenticated: boolean }) => {
  // Enable keyboard shortcuts (requires useNavigate from router context)
  useKeyboardShortcuts();

  // Path-to-hash rewrite runs once in main.tsx via bootstrapHashRouterFromPathname() before createRoot.

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/" element={isAuthenticated ? <SmartAuthRedirect /> : <Index />} />
        <Route path="/login" element={isAuthenticated ? <SmartAuthRedirect /> : <Login />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/register" element={isAuthenticated ? <SmartAuthRedirect /> : <Register />} />
        <Route path="/forgot-password" element={isAuthenticated ? <SmartAuthRedirect /> : <ForgotPassword />} />
        <Route path="/reset-password" element={isAuthenticated ? <SmartAuthRedirect /> : <ResetPassword />} />
        <Route path="/verify-email-change" element={isAuthenticated ? <VerifyEmailChange /> : <Navigate to="/login" />} />
        <Route path="/preferences" element={isAuthenticated ? <Preferences /> : <Navigate to="/login" />} />
        <Route path="/weekly-planner" element={isAuthenticated ? <WeeklyPlanner /> : <Navigate to="/login" />} />
        <Route path="/inici" element={isAuthenticated ? <Home /> : <Navigate to="/login" />} />
        <Route path="/inspiracio" element={isAuthenticated ? <Inspiration /> : <Navigate to="/login" />} />
        <Route path="/recipe/:id" element={isAuthenticated ? <Recipe /> : <Navigate to="/login" />} />
        <Route path="/shopping-list" element={isAuthenticated ? <ShoppingList /> : <Navigate to="/login" />} />
        <Route path="/checkout/address" element={isAuthenticated ? <CheckoutAddress /> : <Navigate to="/login" />} />
        <Route path="/orders" element={isAuthenticated ? <Orders /> : <Navigate to="/login" />} />
        <Route path="/orders/:orderId" element={isAuthenticated ? <OrderDetails /> : <Navigate to="/login" />} />
        <Route path="/payment-success" element={isAuthenticated ? <PaymentSuccess /> : <Navigate to="/login" />} />
        <Route path="/profile" element={isAuthenticated ? <Profile /> : <Navigate to="/login" />} />
        <Route path="/about" element={<About />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/cookie-policy" element={<CookiePolicy />} />
        <Route path="/legal-notice" element={<LegalNotice />} />

        {/* Admin routes - Protected */}
        <Route path="/admin" element={isAuthenticated ? <AdminDashboard /> : <Navigate to="/login" />} />
        <Route path="/admin/weekly-plans" element={isAuthenticated ? <AdminWeeklyPlans /> : <Navigate to="/login" />} />
        <Route path="/admin/orders" element={isAuthenticated ? <AdminOrders /> : <Navigate to="/login" />} />
        <Route path="/admin/users" element={isAuthenticated ? <AdminUsers /> : <Navigate to="/login" />} />
        <Route path="/admin/recipes" element={isAuthenticated ? <AdminRecipes /> : <Navigate to="/login" />} />
        <Route
          path="/admin/canonical-ingredients"
          element={isAuthenticated ? <AdminCanonicalIngredients /> : <Navigate to="/login" />}
        />
        <Route path="/admin/allergens" element={isAuthenticated ? <AdminAllergens /> : <Navigate to="/login" />} />
        <Route
          path="/admin/recipe-coverage"
          element={isAuthenticated ? <AdminRecipeCoverage /> : <Navigate to="/login" />}
        />
        <Route path="/admin/settings" element={<NotFound />} />
        <Route path="/admin/*" element={<NotFound />} />

        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
};

const AppRoutes = () => {
  const { isAuthenticated, loading } = useAuth();

  // Ensure language persists across all navigation
  useLanguagePersistence();

  // Show loading state while checking authentication
  if (loading) {
    return (
      <main className="flex items-center justify-center min-h-screen" role="main" aria-label="Authenticating">
        <div className="text-center">
          <div
            className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"
            role="status"
            aria-live="polite"
          ></div>
          <p className="mt-4 text-foreground/70">Loading...</p>
        </div>
      </main>
    );
  }

  return (
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <HashRouter
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        <ScrollToTop />
        <AnalyticsManager />
        <MetaPixelManager />
        <RoutesWithHooks isAuthenticated={isAuthenticated} />
        <ConditionalLegalFooter />
        <CookieConsentManager />
      </HashRouter>
    </TooltipProvider>
  );
};

const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </QueryClientProvider>
  );
};

export default App;
